import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { Modal } from "../ui/modal";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Button from "../ui/button/Button";
import { useAuthStore } from "@/store/useAuthStore";
import { handleApiError } from "@/services/api";

export default function UserDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const { user, farm, logout, changePIN } = useAuthStore();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isOwner = user?.role === "owner";

  // Change PIN modal state
  const [changePinOpen, setChangePinOpen] = useState(false);
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  function toggleDropdown() {
    setIsOpen(!isOpen);
  }

  function closeDropdown() {
    setIsOpen(false);
  }

  function handleSignOut() {
    closeDropdown();
    logout();
    navigate("/signin", { replace: true });
  }

  function openChangePinModal() {
    closeDropdown();
    setCurrentPin("");
    setNewPin("");
    setConfirmPin("");
    setError("");
    setSuccess(false);
    setChangePinOpen(true);
  }

  function closeChangePinModal() {
    setChangePinOpen(false);
  }

  async function handleChangePIN(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (newPin !== confirmPin) {
      setError("New PIN and Confirm PIN do not match");
      return;
    }

    setSaving(true);
    try {
      await changePIN(currentPin, newPin);
      setSuccess(true);
      setTimeout(() => {
        closeChangePinModal();
      }, 1500);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  }

  const pinFilter = (v: string) => v.replace(/\D/g, "").slice(0, 6);

  // Initials for avatar
  const initials = user?.name
    ? user.name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        className="flex items-center text-gray-700 dropdown-toggle dark:text-gray-400"
      >
        <span className="mr-3 flex h-11 w-11 items-center justify-center rounded-full bg-brand-500 text-sm font-semibold text-white">
          {initials}
        </span>

        <span className="block mr-1 font-medium text-theme-sm">
          {user?.name || "User"}
        </span>
        <svg
          className={`stroke-gray-500 dark:stroke-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          width="18"
          height="20"
          viewBox="0 0 18 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.3125 8.65625L9 13.3437L13.6875 8.65625"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className="absolute right-0 mt-[17px] flex w-[260px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark"
      >
        <div>
          <span className="block font-medium text-gray-700 text-theme-sm dark:text-gray-400">
            {user?.name}
          </span>
          {!isAdmin && (
            <span className="mt-0.5 block text-theme-xs text-gray-500 dark:text-gray-400">
              {farm?.name}
            </span>
          )}
        </div>

        <div className="mt-2 border-t border-gray-200 dark:border-gray-800" />

        {/* Farm settings are the OWNER's: the API refuses everyone else's save,
            and the page shows the farm's bank details. */}
        {isOwner && (
          <Link
            to="/farm-settings"
            onClick={closeDropdown}
            className="flex items-center gap-3 px-3 py-2 mt-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
          >
            <svg
              className="fill-gray-500 group-hover:fill-gray-700 dark:group-hover:fill-gray-300"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.9999 2.25C11.1715 2.25 10.5 2.92157 10.5 3.75V4.09849C10.5 4.66052 10.1374 5.15822 9.61352 5.37929C9.52457 5.41682 9.43656 5.45619 9.34952 5.49734C8.83669 5.74003 8.22637 5.70649 7.76594 5.36214L7.50483 5.16682C6.85083 4.67719 5.92067 4.74352 5.34629 5.31789L5.31789 5.34629C4.74352 5.92067 4.67719 6.85083 5.16682 7.50483L5.36214 7.76594C5.70649 8.22637 5.74003 8.83669 5.49734 9.34952C5.45619 9.43656 5.41682 9.52457 5.37929 9.61352C5.15822 10.1374 4.66052 10.5 4.09849 10.5H3.75C2.92157 10.5 2.25 11.1716 2.25 12V12C2.25 12.8284 2.92157 13.5 3.75 13.5H4.09849C4.66052 13.5 5.15822 13.8626 5.37929 14.3865C5.41682 14.4754 5.45619 14.5634 5.49734 14.6505C5.74003 15.1633 5.70649 15.7736 5.36214 16.2341L5.16682 16.4952C4.67719 17.1492 4.74352 18.0793 5.31789 18.6537L5.34629 18.6821C5.92067 19.2565 6.85083 19.3228 7.50483 18.8332L7.76594 18.6379C8.22637 18.2935 8.83669 18.26 9.34952 18.5027C9.43656 18.5438 9.52457 18.5832 9.61352 18.6207C10.1374 18.8418 10.5 19.3395 10.5 19.9015V20.25C10.5 21.0784 11.1716 21.75 12 21.75C12.8284 21.75 13.5 21.0784 13.5 20.25V19.9015C13.5 19.3395 13.8626 18.8418 14.3865 18.6207C14.4754 18.5832 14.5634 18.5438 14.6505 18.5027C15.1633 18.26 15.7736 18.2935 16.2341 18.6379L16.4952 18.8332C17.1492 19.3228 18.0793 19.2565 18.6537 18.6821L18.6821 18.6537C19.2565 18.0793 19.3228 17.1492 18.8332 16.4952L18.6379 16.2341C18.2935 15.7736 18.26 15.1633 18.5027 14.6505C18.5438 14.5634 18.5832 14.4754 18.6207 14.3865C18.8418 13.8626 19.3395 13.5 19.9015 13.5H20.25C21.0784 13.5 21.75 12.8284 21.75 12C21.75 11.1716 21.0784 10.5 20.25 10.5H19.9015C19.3395 10.5 18.8418 10.1374 18.6207 9.61352C18.5832 9.52457 18.5438 9.43656 18.5027 9.34952C18.26 8.83669 18.2935 8.22637 18.6379 7.76594L18.8332 7.50483C19.3228 6.85083 19.2565 5.92067 18.6821 5.34629L18.6537 5.31789C18.0793 4.74352 17.1492 4.67719 16.4952 5.16682L16.2341 5.36214C15.7736 5.70649 15.1633 5.74003 14.6505 5.49734C14.5634 5.45619 14.4754 5.41682 14.3865 5.37929C13.8626 5.15822 13.5 4.66052 13.5 4.09849V3.75C13.5 2.92157 12.8284 2.25 11.9999 2.25ZM12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                fill=""
              />
            </svg>
            Farm Settings
          </Link>
        )}

        <button
          onClick={openChangePinModal}
          className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
        >
          <svg
            className="fill-gray-500 group-hover:fill-gray-700 dark:group-hover:fill-gray-300"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 2.25C9.37665 2.25 7.25 4.37665 7.25 7V10.25H6.5C5.25736 10.25 4.25 11.2574 4.25 12.5V19.5C4.25 20.7426 5.25736 21.75 6.5 21.75H17.5C18.7426 21.75 19.75 20.7426 19.75 19.5V12.5C19.75 11.2574 18.7426 10.25 17.5 10.25H16.75V7C16.75 4.37665 14.6234 2.25 12 2.25ZM15.25 10.25V7C15.25 5.20507 13.7949 3.75 12 3.75C10.2051 3.75 8.75 5.20507 8.75 7V10.25H15.25ZM12 14.25C12.4142 14.25 12.75 14.5858 12.75 15V17C12.75 17.4142 12.4142 17.75 12 17.75C11.5858 17.75 11.25 17.4142 11.25 17V15C11.25 14.5858 11.5858 14.25 12 14.25Z"
              fill=""
            />
          </svg>
          Change PIN
        </button>

        <button
          onClick={handleSignOut}
          className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
        >
          <svg
            className="fill-gray-500 group-hover:fill-gray-700 dark:group-hover:fill-gray-300"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M15.1007 19.247C14.6865 19.247 14.3507 18.9112 14.3507 18.497L14.3507 14.245H12.8507V18.497C12.8507 19.7396 13.8581 20.747 15.1007 20.747H18.5007C19.7434 20.747 20.7507 19.7396 20.7507 18.497L20.7507 5.49609C20.7507 4.25345 19.7433 3.24609 18.5007 3.24609H15.1007C13.8581 3.24609 12.8507 4.25345 12.8507 5.49609V9.74501L14.3507 9.74501V5.49609C14.3507 5.08188 14.6865 4.74609 15.1007 4.74609L18.5007 4.74609C18.9149 4.74609 19.2507 5.08188 19.2507 5.49609L19.2507 18.497C19.2507 18.9112 18.9149 19.247 18.5007 19.247H15.1007ZM3.25073 11.9984C3.25073 12.2144 3.34204 12.4091 3.48817 12.546L8.09483 17.1556C8.38763 17.4485 8.86251 17.4487 9.15549 17.1559C9.44848 16.8631 9.44863 16.3882 9.15583 16.0952L5.81116 12.7484L16.0007 12.7484C16.4149 12.7484 16.7507 12.4127 16.7507 11.9984C16.7507 11.5842 16.4149 11.2484 16.0007 11.2484L5.81528 11.2484L9.15585 7.90554C9.44864 7.61255 9.44847 7.13767 9.15547 6.84488C8.86248 6.55209 8.3876 6.55226 8.09481 6.84525L3.52309 11.4202C3.35673 11.5577 3.25073 11.7657 3.25073 11.9984Z"
              fill=""
            />
          </svg>
          Sign out
        </button>
      </Dropdown>

      {/* Change PIN Modal */}
      <Modal
        isOpen={changePinOpen}
        onClose={closeChangePinModal}
        className="max-w-sm p-5 sm:p-8 mx-4"
      >
        {success ? (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-50 dark:bg-success-500/10">
              <svg
                className="h-6 w-6 text-success-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-800 dark:text-white/90">
              PIN changed successfully
            </p>
          </div>
        ) : (
          <>
            <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
              Change PIN
            </h4>

            {error && (
              <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
                {error}
              </div>
            )}

            <form onSubmit={handleChangePIN} className="space-y-4">
              <div>
                <Label htmlFor="currentPin">Current PIN</Label>
                <Input
                  id="currentPin"
                  type="password"
                  inputMode="numeric"
                  value={currentPin}
                  onChange={(e) => setCurrentPin(pinFilter(e.target.value))}
                  placeholder="Enter current 6-digit PIN"
                  required
                />
              </div>
              <div>
                <Label htmlFor="newPin">New PIN</Label>
                <Input
                  id="newPin"
                  type="password"
                  inputMode="numeric"
                  value={newPin}
                  onChange={(e) => setNewPin(pinFilter(e.target.value))}
                  placeholder="Enter new 6-digit PIN"
                  required
                />
              </div>
              <div>
                <Label htmlFor="confirmPin">Confirm New PIN</Label>
                <Input
                  id="confirmPin"
                  type="password"
                  inputMode="numeric"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(pinFilter(e.target.value))}
                  placeholder="Re-enter new PIN"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={closeChangePinModal}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={
                    saving ||
                    currentPin.length !== 6 ||
                    newPin.length !== 6 ||
                    confirmPin.length !== 6
                  }
                >
                  {saving ? "Changing..." : "Change PIN"}
                </Button>
              </div>
            </form>
          </>
        )}
      </Modal>
    </div>
  );
}
